import {Badge, Card, Link, Table, Title} from '@yoast/ui-library';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { QueryResultDetailed } from '../../api/generated/insights.schemas';

interface ScanDetailsCardProps {
  queryResult: QueryResultDetailed;
}

export const ScanDetailsCard = ({ queryResult }: ScanDetailsCardProps) => {
  const [hoveredBrandId, setHoveredBrandId] = useState<string | null>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  const responseRef = useRef<HTMLDivElement>(null);

  const response = queryResult.response || 'No response available';

  const mentionedBrands = useMemo(() => queryResult.mentionedBrands || [], [queryResult.mentionedBrands]);
  const citations = queryResult.citations || [];
  const sentiments = queryResult.sentiments || [];

  const activeBrandId = selectedBrandId !== null ? selectedBrandId : hoveredBrandId;

  const highlightedResponse = useMemo(() => {
    if (activeBrandId === null) return response;

    const brand = mentionedBrands.find(b => b.id === activeBrandId);
    if (!brand) return response;

    return response.replaceAll(brand.name, `**${brand.name}**`);
  }, [response, activeBrandId, mentionedBrands]);

  const handleBrandClick = (brandId: string) => {
    setSelectedBrandId(selectedBrandId === brandId ? null : brandId);
  };

  // Grow the container height if the content height increases and never shrink it. This prevents continuous layout shifts when highlighting brands.
  useEffect(() => {
    if (responseRef.current && activeBrandId !== null) {
      const height = responseRef.current.scrollHeight;
	  if( containerHeight === null || height > containerHeight) {
        setContainerHeight(height);
	  }
    }
  }, [activeBrandId, highlightedResponse, containerHeight]);

  const uniqueBrands = Array.from(new Map(mentionedBrands.map(b => [b.id, b])).values());

  return (
    <Card className="yst-mb-4">
      <Card.Content className="yst-p-4">
        <details className="yst-group">
          <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden">
            <div className="yst-flex-1">
              <Title size="3" className="yst-mb-2">{queryResult.query}</Title>
              <div className="yst-flex yst-gap-4 yst-text-sm yst-text-gray-600">
                {mentionedBrands.length > 0 && <span>{uniqueBrands.length} brand{uniqueBrands.length !== 1 ? 's' : ''}</span>}
                {citations.length > 0 && <span>{citations.length} citation{citations.length !== 1 ? 's' : ''}</span>}
                {sentiments.length > 0 && <span>{sentiments.length} sentiment{sentiments.length !== 1 ? 's' : ''}</span>}
              </div>
            </div>
            <div className="yst-text-gray-500 group-open:yst-rotate-90 yst-transition-transform">
              ▶
            </div>
          </summary>

          <div className="yst-mt-6 yst-flex yst-flex-col yst-gap-6">
            {/* AI Response Section */}
            <details className="yst-border-t yst-pt-4">
              <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden yst-font-semibold yst-mb-2">
                <span>AI Response</span>
                <span className="yst-text-gray-500">▶</span>
              </summary>
              <div className="yst-mt-2">
                <div className="yst-rounded-lg yst-border yst-border-gray-300 yst-bg-gray-50 yst-p-4">
                  <div
                    ref={responseRef}
                    className="yst-text-sm yst-text-gray-700 [&_p]:yst-mb-4 [&_ul]:yst-list-disc [&_ul]:yst-ml-6 [&_ul]:yst-mb-4 [&_ol]:yst-list-decimal [&_ol]:yst-ml-6 [&_ol]:yst-mb-4 [&_li]:yst-mb-1 [&_h1]:yst-text-xl [&_h1]:yst-font-bold [&_h1]:yst-mb-2 [&_h2]:yst-text-lg [&_h2]:yst-font-bold [&_h2]:yst-mb-2 [&_h3]:yst-font-bold [&_h3]:yst-mb-2 [&_code]:yst-bg-white [&_code]:yst-px-1 [&_code]:yst-rounded [&_pre]:yst-bg-white [&_pre]:yst-p-3 [&_pre]:yst-rounded [&_pre]:yst-mb-4 [&_pre]:yst-overflow-x-auto [&_p:last-child]:yst-mb-0 [&_ul:last-child]:yst-mb-0 [&_ol:last-child]:yst-mb-0 [&_strong]:yst-bg-yellow-200 [&_strong]:yst-rounded [&_strong]:yst-px-1"
                    style={containerHeight !== null ? { height: `${containerHeight}px` } : undefined}
                  >
                    <ReactMarkdown components={{ a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a> }}>{highlightedResponse}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </details>

            {/* Brands Section */}
            {mentionedBrands.length > 0 && (
              <details className="yst-border-t yst-pt-4">
                <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden yst-font-semibold yst-mb-2">
                  <span>Brands ({uniqueBrands.length})</span>
                  <span className="yst-text-gray-500">▶</span>
                </summary>
                <div className="yst-mt-2 yst-space-y-4">
                  <div>
                    <p className="yst-mb-2 yst-text-sm yst-font-medium">Mentioned brands:</p>
                    <div className="yst-flex yst-flex-wrap yst-gap-2">
                      {uniqueBrands.map((brand) => (
                        <Badge
                          key={brand.id}
                          variant={brand.isSelf ? 'info' : 'plain'}
                          className={`yst-cursor-pointer yst-transition-colors ${
                            activeBrandId === brand.id ? 'yst-ring-2 yst-ring-yellow-400' : ''
                          }`}
                          onMouseEnter={() => setHoveredBrandId(brand.id)}
                          onMouseLeave={() => setHoveredBrandId(null)}
                          onClick={() => handleBrandClick(brand.id)}
                        >
                          {brand.name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="yst-mb-2 yst-text-sm yst-font-medium">Brand ranking:</p>
                    <ol className="yst-list-decimal yst-list-inside yst-space-y-3">
                      {mentionedBrands
                        .sort((a, b) => a.rank - b.rank)
                        .map((brand) => (
                          <li
                            key={`${brand.id}-${brand.rank}`}
                            className="yst-text-sm"
                          >
                            <Badge
                              variant={brand.isSelf ? 'info' : 'plain'}
                              className={`yst-cursor-pointer yst-transition-colors ${
                                activeBrandId === brand.id ? 'yst-ring-2 yst-ring-yellow-400' : ''
                              }`}
                              onMouseEnter={() => setHoveredBrandId(brand.id)}
                              onMouseLeave={() => setHoveredBrandId(null)}
                              onClick={() => handleBrandClick(brand.id)}
                            >
                              {brand.name}
                            </Badge>
                            <p className="yst-ml-6 yst-mt-1 yst-text-sm yst-text-gray-600 yst-italic">
                              "{brand.context}"
                            </p>
                          </li>
                        ))}
                    </ol>
                  </div>
                </div>
              </details>
            )}

            {/* Citations Section */}
            {citations.length > 0 && (
              <details className="yst-border-t yst-pt-4">
                <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden yst-font-semibold yst-mb-2">
                  <span>Citations ({citations.length})</span>
                  <span className="yst-text-gray-500">▶</span>
                </summary>
                <div className="yst-mt-2">
                  <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>Rank</Table.Header>
                  <Table.Header>Domain</Table.Header>
                  <Table.Header>URL</Table.Header>
                  <Table.Header>Associated brands</Table.Header>
                </Table.Row>
              </Table.Head>
            <Table.Body>
              {citations
                .sort((a, b) => a.rank - b.rank)
                .map((citation, index) => (
                  <Table.Row key={`${citation.url}-${index}`}>
                    <Table.Cell>#{citation.rank}</Table.Cell>
                    <Table.Cell>{citation.rootDomain}</Table.Cell>
                    <Table.Cell>
                      <Link href={citation.url} target="_blank" rel="noopener noreferrer">
                        {citation.url}
                      </Link>
                    </Table.Cell>
                    <Table.Cell>
                      {citation.associatedBrands.length > 0 ? (
                        <div className="yst-flex yst-flex-wrap yst-gap-1">
                          {citation.associatedBrands.map((brand) => (
                            <Badge
                              key={brand.id}
                              variant={brand.isSelf ? 'info' : 'plain'}
                              className={`yst-cursor-pointer yst-transition-colors ${
                                activeBrandId === brand.id ? 'yst-ring-2 yst-ring-yellow-400' : ''
                              }`}
                              onMouseEnter={() => setHoveredBrandId(brand.id)}
                              onMouseLeave={() => setHoveredBrandId(null)}
                              onClick={() => handleBrandClick(brand.id)}
                            >
                              {brand.name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="yst-text-gray-500">None</span>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
                  </Table.Body>
                  </Table>
                </div>
              </details>
            )}

            {/* Sentiment Section */}
            {sentiments.length > 0 && (
              <details className="yst-border-t yst-pt-4">
                <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden yst-font-semibold yst-mb-2">
                  <span>Sentiment Analysis ({sentiments.length})</span>
                  <span className="yst-text-gray-500">▶</span>
                </summary>
                <div className="yst-mt-2 yst-flex yst-flex-col yst-gap-4">
              {sentiments.map((sentiment) => (
                <div
                  key={sentiment.id}
                  className="yst-rounded-lg yst-border yst-border-gray-300 yst-p-4"
                >
                  <div className="yst-flex yst-items-center yst-gap-3 yst-mb-3">
                    <div className="yst-flex yst-items-center yst-gap-2">
                      <span className="yst-text-sm yst-font-semibold">Score:</span>
                      <Badge
                        variant={
                          sentiment.score > 0.3
                            ? 'success'
                            : sentiment.score < -0.3
                              ? 'error'
                              : 'plain'
                        }
                      >
                        {sentiment.score.toFixed(2)}
                      </Badge>
                    </div>
                    <div className="yst-flex yst-items-center yst-gap-2">
                      <span className="yst-text-sm yst-font-semibold">Confidence:</span>
                      <Badge variant="plain">{(sentiment.confidence * 100).toFixed(0)}%</Badge>
                    </div>
                  </div>

                  {sentiment.brands.length > 0 && (
                    <div className="yst-mb-3">
                      <span className="yst-text-sm yst-font-semibold yst-mb-1 yst-block">
                        Brands:
                      </span>
                      <div className="yst-flex yst-flex-wrap yst-gap-1">
                        {sentiment.brands.map((brand) => (
                          <Badge
                            key={brand.id}
                            variant={brand.isSelf ? 'info' : 'plain'}
                            className={`yst-cursor-pointer yst-transition-colors ${
                              activeBrandId === brand.id ? 'yst-ring-2 yst-ring-yellow-400' : ''
                            }`}
                            onMouseEnter={() => setHoveredBrandId(brand.id)}
                            onMouseLeave={() => setHoveredBrandId(null)}
                            onClick={() => handleBrandClick(brand.id)}
                          >
                            {brand.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {sentiment.keywords.length > 0 && (
                    <div>
                      <span className="yst-text-sm yst-font-semibold yst-mb-1 yst-block">
                        Keywords:
                      </span>
                      <div className="yst-flex yst-flex-wrap yst-gap-2">
                        {sentiment.keywords.map((keyword, idx) => (
                          <div
                            key={idx}
                            className="yst-flex yst-items-center yst-gap-1 yst-rounded yst-bg-gray-100 yst-px-2 yst-py-1"
                          >
                            <span className="yst-text-sm">{keyword.keyword}</span>
                            <Badge
                              variant={
                                keyword.isPositive
                                  ? 'success'
                                  : keyword.isNegative
                                    ? 'error'
                                    : 'plain'
                              }
                            >
                              {keyword.sentiment.toFixed(2)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
                </div>
              </details>
            )}

            {/* Raw Response Section */}
            {queryResult.rawResponse && (
              <details className="yst-border-t yst-pt-4">
                <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden yst-font-semibold yst-mb-2">
                  <span>Raw Response</span>
                  <span className="yst-text-gray-500">▶</span>
                </summary>
                <div className="yst-mt-2">
                  <pre className="yst-rounded-lg yst-border yst-border-gray-300 yst-bg-gray-50 yst-p-4 yst-text-xs yst-overflow-x-auto yst-max-h-96 yst-overflow-y-auto">
                    {JSON.stringify(queryResult.rawResponse, null, 2)}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </details>
      </Card.Content>
    </Card>
  );
};
